import apiRequest from "../../api/apiRequest";
import { cachedAPIWrapper, getCachedAPIResponse } from "../../apiCaching/apiWrapper";
import { handleCheckboxChangeUtility, showAndFocusErrors } from "../../apiCaching/POST-Requests/helperModules/handleBookingSubmit";
import { apiConfig } from "../../config/apiConfig";
import { REGEX } from "../../constants/regex";
import { isTouchDevice } from "../../util/device-detection";
import { disclaimerHandleCheck, DisclaimerServiceIdentifier } from "../../util/disclaimerService";
import { DomainIdentifier } from "../../util/domainService";
import { LocalizationIdentifier } from "../../util/localizationService";
import { handleFakeSubmit, hmsProgressBarUpdate, removeAllEventListeners } from "../../util/share";
import { parseDOMString, updateVDomElement, VNode } from "../../util/virtualDom";
import { DonorConfirmation } from "./donor-confirmation";
interface CustomSelectElement extends HTMLDivElement {
    selectButton: HTMLButtonElement;
}
declare global {
    interface Window {
        donorPayload?: Record<string, any>,
        shelterList?: Record<string, any>
    }
    interface Shelter {
        $id: string;
        MsMollyShelterId: number;
        Name: string;
        City: string;
        State: string;
        Zip: string;
        IsActive: boolean;
        DeletedDateTime: null | string;
    }

    interface StateData {
        State: string;
        Data: Shelter[];
    }

    interface StateCodes {
        [key: string]: string;
    }
}

type ExceptType = HTMLButtonElement | HTMLDivElement | null; // Add other types as needed
export class Donation {

    stateList: StateCodes = {};
    private donationFormService!: NodeListOf<HTMLElement>;
    private noteWithRequestBody: any;
    constructor() {
        this.init();
    }

    private static instance: Donation
    static getInstance() {
        if (!Donation.instance) {
            Donation.instance = new Donation();
        }
        return Donation.instance;
    }

    private init(): void {
        try {
            this.addCancelDonationListener();
            this.addSubmitDonationListener();
            this.addIsCurrentDonationListener();
            this.addInputListener();
            (async () => {
                try {
                    await this.getUsState();
                    await this.getShelterList();
                    this.initializeCustomSelect();
                } catch (error) {
                    // Handle any errors that may occur during promise resolution
                    console.error("Error:", error);
                }
            })();
        } catch (error) {
            console.error('An error occurred while adding event listeners:', error);
        }
    }

    public addInputListener(): void {
        const inputFields = document.querySelectorAll('.donation-form input');
        const errors: Record<string, string> = {};
        this.donationFormService = document.querySelectorAll('[id*="donation-form-service-reminder"]');
        inputFields?.forEach(input => {
            input?.addEventListener('input', (event) => {
                const target = event.target as HTMLInputElement;
                const inputValue = target.value;
                switch (input.id) {
                    case 'donation-fname':
                        this.validateFirstName(errors, input.id);
                        break;
                    case 'donation-lname':
                        this.validateLastName(errors, input.id);
                        break;
                    case 'donation-email':
                        this.validateEmailInput(errors, input.id);
                        break;
                    case 'yes-donation-amount':
                    case 'no-donation-amount':
                        target.value = inputValue.replace(/[^\d.]/g, '');
                        this.validateDonationAmountInput(errors, target.value, input.id);
                        break;
                    case 'donation-address':
                        this.validateAddress(errors, input.id);
                        break;
                    case 'donation-city':
                        this.validateCity(errors, input.id);
                        break;
                    case 'donation-zip-code':
                        this.validateZipCode(errors, input.id);
                        input.addEventListener('focusout', this.handleFocusOut.bind(this));
                }
                const errorElement = document.getElementById(`${input.id}-error-msg`);
                if (errorElement) {
                    errorElement.classList.add('hidden');
                }
                input.classList.remove('invalid-field');
                this.displayErrors(errors);
            });

        });
        this.donationFormService?.forEach((checkbox) => {
             checkbox.addEventListener("change", () => handleCheckboxChangeUtility(".donation-form"));
        });
    }

    handleFocusOut() {
        const zipInput = document.getElementById('donation-zip-code') as HTMLInputElement | null;
        if (!zipInput) return;
        const zipCode = zipInput.value.trim();
        if (zipCode) {
            DisclaimerServiceIdentifier(zipCode);
        }
    }

    private validateZipCode(errors: Record<string, string>, id: string) {
        const input = document.getElementById(id) as HTMLInputElement | null;
        if (!input) return;
        const zip = input.value.trim();
        if (zip && !REGEX.sendZip.test(zip)) {
            errors[input.id] = 'Invalid zip code format';
        } else {
            delete errors[id];
        }
    }

    private validateDonationAmountInput(errors: Record<string, string>, amount: string, id: any): void {
        const value = Number(amount);
        let minamount = 5
        const fakeSubmitDom = document.getElementById('donation-fname') as HTMLInputElement | null;
        if(fakeSubmitDom?.value?.toLowerCase() === 'isfakesubmit'){
            minamount = 1;
        }
        if (Number.isNaN(value)) {
            errors[id] = 'Donation amount must be a number.';
        }
        if (value < minamount || value > 2000) {
            errors[id] = `Donation amount must be between ${minamount} and 2000.`;
        } else {
            delete errors[id];
        }
    }

    private validateFirstName(errors: Record<string, string>, id: string): void {
        const inputElement = document.getElementById(id) as HTMLInputElement | null;
        if (!inputElement) return;
        const name = inputElement.value.trim();
        if (name && !REGEX.sendName.test(name) && /^[A-Za-z.'-]+$/.test(name)) {
            errors[id] = 'Invalid first name';
        } else {
            delete errors[id];
        }
    }

    private validateLastName(errors: Record<string, string>, id: string): void {
        const inputElement = document.getElementById(id) as HTMLInputElement | null;
        if (!inputElement) return;
        const name = inputElement.value.trim();
        if (name && !REGEX.sendLname.test(name) && /^[A-Za-z.'-]+$/.test(name)) {
            errors[id] = 'Invalid last name';
        } else {
            delete errors[id];
        }
    }

    private validateEmailInput(errors: Record<string, string>, id: string): void {
        const inputElement = document.getElementById(id) as HTMLInputElement | null;
        if (!inputElement) return;
        const email = inputElement.value.trim();
        if (email && !REGEX.sendEmail.test(email)) {
            errors[id] = 'Invalid email format';
        } else {
            delete errors[id];
        }
    }

    private validateAddress(errors: Record<string, string>, id: string): void {
        const inputElement = document.getElementById(id) as HTMLInputElement | null;
        if (!inputElement) return;
        const address = inputElement.value.trim();
        if (address && !REGEX.sendAddress1.test(address)) {
            errors[id] = 'Please enter valid address';
        } else {
            delete errors[id];
        }
    }

    private validateCity(errors: Record<string, string>, id: string): void {
        const inputElement = document.getElementById(id) as HTMLInputElement | null;
        if (!inputElement) return;
        const city = inputElement.value.trim();
        if (city && !REGEX.sendName.test(city)) {
            errors[id] = 'Please enter valid city';
        } else {
            delete errors[id];
        }
    }

    public addIsCurrentDonationListener(): void {
        const radioButtons = document.getElementsByName('mly-customer-radio');
        if (radioButtons.length > 0) {
            Array.from(radioButtons).forEach((radio) => {
                radio.addEventListener('click', (e) => this.handleRadioClick(e));
            });
        }
    }

    private handleRadioClick(event: Event): void {
        const radioBtnValue = (event?.target as HTMLInputElement)?.value;
        const noCustumerDom = document.getElementById('mlyCustomerNo') as HTMLElement;
        const yesCustumerDom = document.getElementById('mlyCustomerYes') as HTMLElement;
        const updateVisibility = (isFlex: boolean) => {
            if (noCustumerDom) {
                noCustumerDom.style.display = isFlex ? 'flex' : 'none';
            }
            if (yesCustumerDom) {
                yesCustumerDom.style.display = isFlex ? 'none' : 'flex';
            }
        };
        if (radioBtnValue === 'no') {
            updateVisibility(true);
            this.handleShelterListUpdate('no');
        } else if (radioBtnValue === 'yes') {
            updateVisibility(false);
            this.handleShelterListUpdate('yes');
        }
    }

    private handleShelterListUpdate(sectionType: 'no' | 'yes'): void {
        const shelterList: any = window.shelterList;
        if (shelterList?.length > 0) {
            this.createShelterSelectOption(this.sortShelterByStateName(shelterList));
        }
        const selectElement = document.getElementById(`${sectionType}-customer-choose-shelter`) as HTMLSelectElement;
        const stateElement = document.querySelector(`#${sectionType}-stateShelter #choose-state-shelter-${sectionType}`) as HTMLSelectElement;
        const allShelterElement = document.querySelector(`#${sectionType}-allShelter #choose-all-shelter`) as HTMLSelectElement;
        if (selectElement && stateElement && allShelterElement) {
            this.updateButtonStyle(selectElement, stateElement, allShelterElement, sectionType);
        }
    }

    public updateButtonStyle(selectElement: HTMLSelectElement, stateElement: HTMLSelectElement, allShelterElement: HTMLSelectElement, section: string) {
        const selectButton = section === 'no'
            ? document.querySelector('#mlyCustomerNo .custom-select .select-selected') as HTMLButtonElement
            : document.querySelector('#mlyCustomerYes .custom-select .select-selected') as HTMLButtonElement;
        let selectButton1: any;
        let selectButton2: any;

        // Check if stateElement is part of the 'yes' section, and target the '#yes-stateShelter' button if so
        if (stateElement && stateElement?.value !== "" && section === 'yes') {
            selectButton1 = document.querySelector('#yes-stateShelter .custom-select .select-selected') as HTMLButtonElement;
        } else if (stateElement && stateElement?.value !== "" && section === 'no') {
            selectButton1 = document.querySelector('#no-stateShelter .custom-select .select-selected') as HTMLButtonElement;
        }

        // Check if allShelterElement is part of the 'yes' section, and target the '#yes-allShelter' button if so
        if (allShelterElement && allShelterElement.value !== "" && section === 'yes') {
            selectButton2 = document.querySelector('#yes-allShelter .custom-select .select-selected') as HTMLButtonElement;
        } else if (allShelterElement && allShelterElement.value !== "" && section === 'no') {
            selectButton2 = document.querySelector('#no-allShelter .custom-select .select-selected') as HTMLButtonElement;
        }

        const isAnyElementSelected =
            (selectElement && selectElement.value !== "") ||
            (stateElement && stateElement.value !== "") ||
            (allShelterElement && allShelterElement.value !== "");

        const applyButtonStyles = (button: HTMLButtonElement | null) => {
            if (button) {
                const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-500').trim();
                const whiteColor = getComputedStyle(document.documentElement).getPropertyValue('--white').trim();
                button.style.backgroundColor = primaryColor;
                button.style.color = whiteColor;
                button.style.whiteSpace = "nowrap";
                button.style.overflow = "hidden";
                button.style.borderRight = "40px solid var(--primary-500)";
            }
        };

        // Function to reset button styles if no selection is made
        const resetButtonStyles = (button: HTMLButtonElement | null) => {
            if (button) {
                button.style.backgroundColor = "";
                button.style.color = "";
                button.style.whiteSpace = "";
                button.style.overflow = "";
                button.style.borderRight = "";
            }
        };
        if (isAnyElementSelected) {
            // Apply styles to all buttons if any of the elements have a value
            applyButtonStyles(selectButton);
            applyButtonStyles(selectButton1);
            applyButtonStyles(selectButton2);
        } else {
            // Reset styles
            resetButtonStyles(selectButton);
            resetButtonStyles(selectButton1);
            resetButtonStyles(selectButton2);
        }
    }

    public addCancelDonationListener(): void {
        try {
            const cancelDonationBtn = removeAllEventListeners('.donation-form .cancel-verification') as HTMLElement | null;
            cancelDonationBtn?.addEventListener('click', this.cancelDonation);
        } catch (error) {
            console.error('An error occurred while adding the cancel donation listener:', error);
        }
    }

    private cancelDonation(): void {
        if (document.referrer) {
            window.history.back();
        } else {
            window.location.href = window.location.origin;
        }
    }

    public addSubmitDonationListener(): void {
        try {
            const submitDonationBtn = removeAllEventListeners('.donation-form .btn-next') as HTMLElement | null;
            submitDonationBtn?.addEventListener('click', () => this.submitDonation());
        } catch (error) {
            console.error('An error occurred while adding the submit donation listener:', error);
        }
    }


    private getDonorInfo() {
        const elements = document.querySelectorAll(
            '#donation-fname, #donation-lname, #donation-email, #donation-address, #donation-city, #mly-donation-state, #donation-zip-code'
        ) as NodeListOf<HTMLInputElement> | undefined[];
        const donorInfo: Record<string, any> = {};
        elements.forEach((element) => {
            if (element) {
                if (element.type === 'checkbox') {
                    donorInfo[element.id] = element.checked;
                } else {
                    donorInfo[element.id] = element.value.trim();
                }
            }
        });
        
        // Get the checked radio button value for customer status
        const checkedRadio = document.querySelector<HTMLInputElement>('input[name="mly-customer-radio"]:checked');
        const customerRadioValue = checkedRadio?.value ?? '';
        
        return {
            firstName: donorInfo['donation-fname'] ?? '',
            lastName: donorInfo['donation-lname'] ?? '',
            email: donorInfo['donation-email'] ?? '',
            address: donorInfo['donation-address'] ?? '',
            city: donorInfo['donation-city'] ?? '',
            state: donorInfo['mly-donation-state'] ?? '',
            zipCode: donorInfo['donation-zip-code'] ?? '',
            isCurrentCustomer: customerRadioValue === "yes",
        };
    }

    private getShelterInfo(isCurrentCustomer: boolean) {
        const isMlyCustomerInput = document.querySelector<HTMLInputElement>('input[name="mly-customer-radio"]:checked');
        const donorAmtElement = isMlyCustomerInput?.value.toLowerCase() == 'yes'
            ? document.getElementById('yes-donation-amount')
            : document.getElementById('no-donation-amount');
        const amount = donorAmtElement ? Number((donorAmtElement as HTMLInputElement).value) : 0;

        let shelterId: string | null = null;
        let comment = '';

        const extractShelterData = (commentId: string, shelterIdSelector: string | null) => {
            const commentElement = document.getElementById(commentId) as HTMLInputElement | null;
            comment = commentElement ? commentElement.value : comment;

            if (shelterIdSelector) {
                const shelterElement = document.getElementById(shelterIdSelector) as HTMLSelectElement | null;
                shelterId = shelterElement ? shelterElement.value : shelterId;
            }
        };

        const prefix = isCurrentCustomer ? 'yes' : 'no';
        const selectElement = document.getElementById(`${prefix}-customer-choose-shelter`)
        const choice = selectElement ?  (selectElement as HTMLSelectElement).value : ""

        const shelterOptionsMap: Record<string, { commentId: string; shelterIdSelector: string | null }> = {
            'yes-stateShelter': { commentId: 'state-questn-comment', shelterIdSelector: 'choose-state-shelter' },
            'yes-allShelter': { commentId: 'all-questn-comment', shelterIdSelector: 'choose-all-shelter' },
            'yes-localShelter': { commentId: 'local-questn-comment', shelterIdSelector: null },
            'no-stateShelter': { commentId: 'state-questn-comment-no', shelterIdSelector: 'choose-state-shelter-no' },
            'no-allShelter': { commentId: '_national_no_options', shelterIdSelector: 'choose-all-shelter-no' },
            'no-localShelter': { commentId: 'local-questn-comment-no', shelterIdSelector: null },
        };

        const selectedOption = shelterOptionsMap[choice];
        if (selectedOption) {
            extractShelterData(selectedOption.commentId, selectedOption.shelterIdSelector);
        }

        return { amount, shelterId, comment };
    }

    private getCheckboxId(zipCode: string): string {
        const domain = DomainIdentifier();
        const localisation = LocalizationIdentifier();
        const countryZip = !REGEX.sendCaPostalCode.test(zipCode?.trim()) ? 'us' : 'ca';

        if (((domain === "us" && !localisation) || (domain === "us" && localisation === "us")) && countryZip === 'us') {
            return 'donation-form-service-reminder-us';
        } else {
            return 'donation-form-service-reminder-ca';
        }
    }


    /**
     * Submits donation form data after validation
     */
    private submitDonation(): void {
        try {
            // Validate form before proceeding
            if (!this.isValid()) {
                console.log('Form validation failed');
                return;
            }

            // Collect all required data
            const donorInfo = this.collectDonorInfo();
            if (!donorInfo) return;

            const shelterInfo = this.collectShelterInfo(donorInfo.isCurrentCustomer);
            if (!shelterInfo) return;

            // Create the donation payload
            const payload = this.createDonorPayload(donorInfo, shelterInfo);
            
            // Process the donation
            this.processDonation(payload);
            
        } catch (error) {
            console.error('An unexpected error occurred in submitDonation:', error);
        }
    }

    /**
     * Collects donor information from form
     * @returns Donor information object or null if an error occurs
     */
    private collectDonorInfo() {
        try {
            const donorInfo = this.getDonorInfo();
            if (!donorInfo) {
                console.error('Failed to get donor information');
                return null;
            }
            return donorInfo;
        } catch (error) {
            console.error('Error getting donor information:', error);
            return null;
        }
    }

    /**
     * Collects shelter information based on customer status
     * @param isCurrentCustomer Whether the donor is a current customer
     * @returns Shelter information object or null if an error occurs
     */
    private collectShelterInfo(isCurrentCustomer: boolean) {
        try {
            const shelterInfo = this.getShelterInfo(isCurrentCustomer);
            if (!shelterInfo) {
                console.error('Failed to get shelter information');
                return null;
            }
            return shelterInfo;
        } catch (error) {
            console.error('Error getting shelter information:', error);
            return null;
        }
    }

    /**
     * Extracts campaign information from the URL
     * @returns Campaign query string or null if parsing fails
     */
    private extractCampaignInfo(): string | null {
        try {
            return new URL(window.location.href).search ?? null;
        } catch (error) {
            console.error('Error parsing URL:', error);
            return null;
        }
    }

    /**
     * Parses and validates the shelter ID
     * @param shelterId The shelter ID to parse
     * @returns Parsed shelter ID as a number or null if invalid
     */
    private parseShelterID(shelterId: string | undefined): number | null {
        if (!shelterId) return null;
        
        const parsedId = Number(shelterId);
        return !Number.isNaN(parsedId) ? parsedId : null;
    }

    /**
     * Creates the donor payload object from donor and shelter information
     * @param donorInfo Donor information
     * @param shelterInfo Shelter information
     * @returns Donor payload object
     */
    private createDonorPayload(donorInfo: any, shelterInfo: any) {
        // Extract campaign info
        const campaign = this.extractCampaignInfo();
        
        // Parse shelter ID
        const msMollyShelterId = this.parseShelterID(shelterInfo.shelterId);
        
        // Create base payload
        let donorPayload = {
            amount: shelterInfo.amount,
            msMollyShelterId,
            donorFirstName: donorInfo.firstName,
            donorLastName: donorInfo.lastName,
            donorEmail: donorInfo.email,
            donorAddress: donorInfo.address,
            donorCity: donorInfo.city,
            donorState: donorInfo.state,
            donorPostalCode: donorInfo.zipCode ? donorInfo.zipCode.trim() : '',
            isCurrentCustomer: donorInfo.isCurrentCustomer,
            isMobile: isTouchDevice(),
            campaign: campaign,
            comments: shelterInfo.comment || ''
        };
        
        // Apply disclaimer check if applicable
        return this.applyDisclaimerCheck(donorPayload, donorInfo.zipCode);
    }
    
    /**
     * Applies disclaimer check to the donor payload
     * @param donorPayload Base payload object
     * @param zipCode Donor zip code
     * @returns Updated donor payload
     */
    private applyDisclaimerCheck(donorPayload: any, zipCode: string | undefined) {
        if (!zipCode) {
            console.error('Zip code is missing');
            return donorPayload;
        }
        
        try {
            const checkboxId = this.getCheckboxId(zipCode);
            const donationForm = document.querySelector('.donation-form');
            
            if (!donationForm) {
                console.error('Donation form element not found');
                return donorPayload;
            }
            
            this.noteWithRequestBody = disclaimerHandleCheck(
                false, 
                donationForm as HTMLElement, 
                donorPayload, 
                zipCode.trim(), 
                checkboxId
            );
            return this.noteWithRequestBody;
        } catch (error) {
            console.error('An error occurred in disclaimerHandleCheck:', error);
            return donorPayload; // Continue with the process even if disclaimer check fails
        }
    }
    
    /**
     * Processes the donation by updating the UI and initiating the purchase
     * @param donorPayload The donor payload to process
     */
    private processDonation(donorPayload: any) {
        // Set global donor payload
        window.donorPayload = donorPayload;
        console.log('Form is valid');
        
        // Proceed with next step
        this.advanceToNextStep();
        
        // Process purchase if payload is available
        this.initiatePurchase();
    }
    
    /**
     * Advances to the next step in the donation process
     */
    private advanceToNextStep() {
        if (typeof this.switchToNextStep === 'function') {
            this.switchToNextStep();
        } else {
            console.error('switchToNextStep method is not defined');
        }
    }
    
    /**
     * Initiates the purchase process if donor payload is available
     */
    private initiatePurchase() {
        if (!window.donorPayload) {
            console.error('Donor payload is not available for purchase');
            return;
        }
        
        if (typeof this.prePurchaseCertificate === 'function') {
            this.prePurchaseCertificate();
        } else {
            console.error('prePurchaseCertificate method is not defined');
        }
    }

    isFakeSubmitHandler(request: { url: string; method: string; data: Record<string, string> }): void {
        handleFakeSubmit(request)
            .then(() => {
                console.log("Fake Submit Request triggered");
            })
            .catch((err) => {
                console.error(err)
            }).finally(() => {
                const formdata = JSON.stringify(request);
                sessionStorage.setItem('fakeSubmitData', formdata);
                const currentUrl = window.location.href.split("/")
                currentUrl[currentUrl.length - 2] = "confirmation"
                window.location.href = currentUrl.join("/")
            })
    }


    private isValid(): boolean {
        this.hideErrors();
        const errors: Record<string, string> = {};
        this.validateInput(errors, 'donation-fname', 'First Name is required');
        this.validateInput(errors, 'donation-lname', 'Last Name is required');
        this.validateEmail(errors, 'donation-email', 'Valid Email is required');
        this.validateAddressInput(errors, 'donation-address', 'Donation Address is required');
        this.validateZip(errors, 'donation-zip-code', 'Valid Zip Code is required');
        this.validateChecked(errors, 'mly-customer-yes', 'Please confirm if you are a customer');
        this.validateDonationAmount(errors, 'yes-donation-amount', 'no-donation-amount', 'Donation Amount is required');
        this.validateShelter(errors, 'yes-customer-choose-shelter', 'Please choose a shelter');
        this.validateShelterNo(errors, 'no-customer-choose-shelter', 'Please choose a shelter');
        this.validateInput(errors, 'donation-city', 'Please provide a donation city');

        this.validateSelection(errors, 'mly-donation-state', 'Please choose a state');
        this.validateComment(errors, 'state-questn-comment', 'Please provide a comment');
        this.validateComment(errors, 'local-questn-comment', 'Please provide a comment');
        this.validateComment(errors, 'all-questn-comment', 'Please provide a comment');
        this.validateComment(errors, 'state-questn-comment-no', 'Please provide a comment');

        const isCurrentCustomer = (document.getElementById('mly-customer-yes') as HTMLInputElement)?.checked;
        if (isCurrentCustomer) {
            const yesCustumerChooseShelter = (document.getElementById('yes-customer-choose-shelter') as HTMLSelectElement)?.value;
            if (yesCustumerChooseShelter && yesCustumerChooseShelter == 'yes-stateShelter') {

                this.validateSelection(errors, 'choose-state-shelter', 'Please choose a shelter');

            } else if (yesCustumerChooseShelter && yesCustumerChooseShelter == 'yes-allShelter') {

                this.validateSelection(errors, 'choose-all-shelter', 'Please choose a shelter');
            }
        } else {
            const noCustumerChooseShelter = (document.getElementById('no-customer-choose-shelter') as HTMLSelectElement)?.value;
            if (noCustumerChooseShelter && noCustumerChooseShelter == 'no-stateShelter') {

                this.validateSelection(errors, 'choose-state-shelter-no', 'Please choose a shelter');
            } else if (noCustumerChooseShelter && noCustumerChooseShelter == 'no-allShelter') {
                this.validateSelection(errors, 'choose-all-shelter-no', 'Please choose a shelter');
            }
        }

        this.displayErrors(errors);
        return Object.keys(errors).length === 0;
    }

    public hideErrors() {
        document.querySelectorAll('.error-msg').forEach(e => e.classList.add('hidden'));
        document.querySelectorAll('.invalid-field').forEach(e => e.classList.remove('invalid-field'));
    }

    private validateInput(errors: Record<string, string>, id: string, errorMessage: string) {
        const input = document.getElementById(id) as HTMLInputElement | null;
        const errorMsgSpan = document.getElementById(`${id}-error-msg`);

        if (input && errorMsgSpan) {
            if (!input.value && input.required) {
                errors[input.id] = errorMsgSpan.innerHTML ?? errorMessage;
            } else if (input.value && !REGEX.sendName.test(input.value)) {
                errors[input.id] = errorMessage;
            }
        }
    }

    private validateAddressInput(errors: Record<string, string>, id: string, errorMessage: string) {
        const input = document.getElementById(id) as HTMLInputElement | null;
        const errorMsgSpan = document.getElementById(`${id}-error-msg`);

        if (input && errorMsgSpan) {
            if (!input.value && input.required) {
                errors[input.id] = errorMsgSpan.innerHTML ?? errorMessage;
            } else if (input.value && !REGEX.sendAddress1.test(input.value)) {
                errors[input.id] = errorMessage;
            }
        }
    }

    private validateEmail(errors: Record<string, string>, id: string, errorMessage: string) {
        const input = document.getElementById(id) as HTMLInputElement | null;
        const errorMsgSpan = document.getElementById(`${id}-error-msg`);

        if (input && errorMsgSpan) {
            if (input.required && !input.value || !REGEX.sendEmail.test(input.value)) {
                errors[input.id] = errorMsgSpan.innerHTML ?? errorMessage;
            }
        }
    }

    private validateZip(errors: Record<string, string>, id: string, errorMessage: string) {
        const input = document.getElementById(id) as HTMLInputElement | null;
        const errorMsgSpan = document.getElementById(`${id}-error-msg`);

        if (input && errorMsgSpan) {
            if (!input.value && input.required) {
                errors[input.id] = errorMsgSpan.innerHTML ?? errorMessage;
            }

            if (input.value && !REGEX.sendZip.test(input.value.trim())) {
                errors[input.id] = 'Invalid zip code format';
            }
        }
    }

    private validateChecked(errors: Record<string, string>, id: string, errorMessage: string) {
        const input = document.querySelector('input[name="mly-customer-radio"]:checked');
        const errorMsgSpan = document.getElementById(`${id}-error-msg`);

        if (input && errorMsgSpan) {
            if (!(input as HTMLInputElement).checked && (input as HTMLInputElement).required) {
                errors['customertype-error-msg'] = errorMsgSpan.innerHTML ?? errorMessage;
            }
        }
    }


    private validateDonationAmount(errors: Record<string, string>, id1: string, id2: string, errorMessage: string) {
        const input1 = document.getElementById(id1) as HTMLInputElement | null;
        const input2 = document.getElementById(id2) as HTMLInputElement | null;
        const errorMsgSpan1 = document.getElementById(`${id1}-error-msg`);
        const errorMsgSpan2 = document.getElementById(`${id2}-error-msg`);
        const isMlyCustmer = document.querySelector('input[name="mly-customer-radio"]:checked');

        if (input1 && errorMsgSpan1 && isMlyCustmer && (isMlyCustmer as HTMLInputElement).value === 'yes' && !input1.value && input1.required) {
            errors[input1.id] = errorMsgSpan1.innerHTML ?? errorMessage;
        }

        if (input2 && errorMsgSpan2 && isMlyCustmer && (isMlyCustmer as HTMLInputElement).value === 'no' && !input2.value && input2.required) {
            errors[input2.id] = errorMsgSpan2.innerHTML ?? errorMessage;
        }
    }

    private validateShelterNo(errors: Record<string, string>, id1: string, errorMessage: string) {
        const isMlyCustmer = document.querySelector('input[name="mly-customer-radio"]:checked');

        if (isMlyCustmer && (isMlyCustmer as HTMLInputElement).value === 'no') {
            const input1 = document.getElementById(id1) as HTMLInputElement | null;
            const errorMsgSpan = document.getElementById(`${id1}-error-msg`);

            if (input1 && errorMsgSpan && !input1.value && input1.required) {
                errors[input1.id] = errorMsgSpan.innerHTML ?? errorMessage;
            }
        }
    }

    private validateShelter(errors: Record<string, string>, id1: string, errorMessage: string) {
        const isMlyCustmer = document.querySelector('input[name="mly-customer-radio"]:checked');
        const input1 = document.getElementById(id1) as HTMLInputElement | null;

        if (isMlyCustmer && (isMlyCustmer as HTMLInputElement).value === 'yes' && input1 && !input1.value && input1.required) {
            const errorMsgSpan = document.getElementById(`${id1}-error-msg`);
            if (errorMsgSpan) {
                errors[input1.id] = errorMsgSpan.innerHTML ?? errorMessage;
            }
        }

        const input2 = document.getElementById('choose-state-shelter') as HTMLSelectElement | null;
        if (input1 && input1.value === 'yes-stateShelter' && input2 && !input2.value && input2.required) {
            const errorMsgSpan = document.getElementById(`${id1}-error-msg`);
            if (errorMsgSpan) {
                errors[input2.id] = errorMsgSpan.innerHTML ?? errorMessage;
            }
        }
    }

    private validateSelection(errors: Record<string, string>, id: string, errorMessage: string) {
        const input1 = document.getElementById(id);
        const errorMsgSpan = document.getElementById(`${id}-error-msg`);

        if (input1 && errorMsgSpan && !(input1 as HTMLSelectElement).value && (input1 as HTMLSelectElement).required) {
            errors[input1.id] = errorMsgSpan.innerHTML ?? errorMessage;
        }
    }

    private validateComment(errors: Record<string, string>, id: string, errorMessage: string) {
        const input1 = document.getElementById(id) as HTMLInputElement | null;

        if (input1) {
            if (input1.value && input1.value.length > 500) {
                errors[input1.id] = 'Comments cannot exceed 500 characters';
            }
        }
    }


    private displayErrors(errors: Record<string, string>) {
     showAndFocusErrors(errors);
    }

    createCustomSelect(customSelectParents: any) {
        const customSelects: NodeListOf<CustomSelectElement> = customSelectParents || document.querySelectorAll(".donation-form .custom-select");

        customSelects.forEach(select => {
            const selectElement: any = select.querySelector("select");
            const selectOptions: NodeListOf<HTMLOptionElement> = selectElement.querySelectorAll("option");
            const selectedOption: string = selectOptions[selectElement.selectedIndex].innerHTML;
            const selectButton: HTMLButtonElement = document.createElement("button");
            selectButton.type = "button";
            selectButton.classList.add("select-selected");
            selectButton.innerHTML = selectedOption;
            select.appendChild(selectButton);
            const optionsContainer: HTMLDivElement = document.createElement("div");
            optionsContainer.classList.add("select-items", "select-hide", "slim-scroll");
            selectOptions.forEach(option => {
                let self = this;
                if (option.index !== 0) {
                    const optionButton: HTMLButtonElement = document.createElement("button");
                    optionButton.type = "button";
                    optionButton.innerHTML = option.innerHTML;
                    optionButton.value = option.value;
                    if (option.disabled) {
                        optionButton.disabled = true;
                        optionButton.style.cursor = "not-allowed";
                        optionButton.style.opacity = "0.5";
                    }
                    else {
                        optionButton.addEventListener("click", function (e: any) {
                            const selectedText: any = e.target?.innerHTML;
                            selectButton.innerHTML = selectedText;
                            selectElement.value = e.target?.value;
                            self.closeAllSelect(selectButton);
                            const isMlyCustmer = (document.querySelector('input[name="mly-customer-radio"]:checked') as HTMLInputElement)?.value;
                            if (e?.target.value === `${isMlyCustmer}-allShelter`) {
                                const chooseAllState = document.querySelectorAll(`#${isMlyCustmer}-stateShelter,#${isMlyCustmer}-localShelter`);
                                self.hideShelterDom(chooseAllState);
                            }
                            if (e?.target.value === `${isMlyCustmer}-stateShelter`) {
                                const chooseAllState = document.querySelectorAll(`#${isMlyCustmer}-allShelter,#${isMlyCustmer}-localShelter`);
                                self.hideShelterDom(chooseAllState);
                            }
                            if (e?.target.value === `${isMlyCustmer}-localShelter`) {
                                const chooseAllState = document.querySelectorAll(`#${isMlyCustmer}-stateShelter,#${isMlyCustmer}-allShelter`);
                                self.hideShelterDom(chooseAllState);
                            }

                            if (select.querySelector("#no-customer-choose-shelter")) {
                                self.hideErrorMessage('no-customer-choose-shelter');
                            }
                            if (select.querySelector("#yes-customer-choose-shelter")) {
                                const selected = document.getElementById(`${e.target?.value}`) as HTMLElement;
                                if (selected) {
                                    selected.classList.remove("hidden");
                                    selected.style.display = "flex";
                                    const selectElement = document.querySelector("#yes-customer-choose-shelter") as HTMLSelectElement;

                                    if (selectElement) {
                                        const options = selectElement.options;
                                        for (const element of options) {
                                            if (element.value && element.value !== e.target?.value) {
                                                const selected = document.getElementById(`${element.value}`) as HTMLElement;
                                                if (selected) {
                                                    selected.style.display = '';
                                                }
                                            }
                                        }
                                    }


                                }
                                self.hideErrorMessage('yes-customer-choose-shelter');
                                if (e.target?.value === 'yes-stateShelter') {
                                    const stateVal = (document.getElementById('mly-donation-state') as HTMLSelectElement)?.value;
                                    self.createStateSelectOption(stateVal);
                                }
                            }
                            if (select.querySelector("#no-customer-choose-shelter")) {
                                const selected = document.getElementById(`${e.target?.value}`) as HTMLElement;
                                if (selected) {
                                    selected.classList.remove("hidden");
                                    selected.style.display = "flex";
                                    const selectElement = document.querySelector("#no-customer-choose-shelter") as HTMLSelectElement;

                                    if (selectElement) {
                                        const options = selectElement.options;
                                        for (const element of options) {
                                            if (element.value && element.value !== e.target?.value) {
                                                const selected = document.getElementById(`${element.value}`) as HTMLElement;
                                                if (selected) {
                                                    selected.style.display = '';
                                                }
                                            }
                                        }
                                    }
                                }
                                if (e.target?.value === 'no-stateShelter') {
                                    const stateVal = (document.getElementById('mly-donation-state') as HTMLSelectElement)?.value;
                                    self.createStateSelectOption(stateVal);
                                }

                                self.hideErrorMessage('no-customer-choose-shelter');
                            }

                            if (select.querySelector("#choose-state-shelter")) {
                                self.hideErrorMessage('choose-state-shelter');
                            }
                            if (select.querySelector("#choose-state-shelter-no")) {
                                self.hideErrorMessage('choose-state-shelter-no');
                            }
                            if (select.querySelector("#choose-all-shelter-no")) {
                                self.hideErrorMessage('choose-all-shelter-no');
                            }

                            if (select.querySelector("#choose-all-shelter")) {
                                self.hideErrorMessage('choose-all-shelter');
                            }

                            if (select.querySelector("#mly-donation-state")) {
                                self.hideErrorMessage('mly-donation-state');
                                self.createStateSelectOption(e.target?.value);

                            }
                        });
                    }

                    optionsContainer.appendChild(optionButton);
                }
            });
            select.appendChild(optionsContainer);
            let self = this;
            selectButton.addEventListener("click", function (e: MouseEvent) {
                e.stopPropagation();
                self.closeAllSelect(this);
                const nextSibling = this.nextSibling as HTMLDivElement | null;
                if (nextSibling) {
                    nextSibling.classList.toggle("select-hide");
                }
                this.classList.toggle("select-arrow-active");

                if (!this.classList.contains("select-arrow-active")) {
                    if (selectButton?.nextSibling) {
                        (selectButton.nextSibling as HTMLDivElement)?.classList.add("select-hide");
                    }
                }

                document.addEventListener('keydown', (event) => {
                    if (event.key === 'Escape') {
                        nextSibling?.classList.add("select-hide");
                        selectButton.classList.remove("select-arrow-active");
                    }
                });

                selectButton.style.backgroundColor = "var(--primary-500)";
                selectButton.style.color = "var(--white)";
                selectButton.style.whiteSpace = "nowrap";
                selectButton.style.overflow = "hidden";
                selectButton.style.borderRight = "40px solid var(--primary-500)";

            });
        });
    }

    hideShelterDom(shelterDomList: any) {
        shelterDomList?.forEach((element: any) => {
            element?.classList.add("hidden");
        });
    }

    // Function to close all select boxes
    closeAllSelect(except: ExceptType) {
        const allSelectItems: NodeListOf<HTMLDivElement> = document.querySelectorAll(".donation-form .select-items.slim-scroll");
        const allSelectButtons: NodeListOf<HTMLButtonElement> = document.querySelectorAll(".donation-form .select-selected");
        allSelectItems.forEach((item, index) => {
            if (except !== allSelectButtons[index]) {
                item.classList.add("select-hide");
                allSelectButtons[index]?.classList?.remove("select-arrow-active");
                if (allSelectButtons[index]?.nextElementSibling) {
                    allSelectButtons[index]?.nextElementSibling?.classList.add("select-hide");
                }
            }

        });


    }

    // Main function to initialize custom select elements
    initializeCustomSelect() {
        let elements = document.querySelectorAll('.donation-form .select-selected') as NodeList;
        elements.forEach(function (element) {
            if (element.parentNode) {
                element.parentNode.removeChild(element);
            }
        });
        let selectItems = document.querySelectorAll('.donation-detail-wrap .select-items.select-hide.slim-scroll');
        selectItems.forEach(function (item) {
            if (item.parentNode) {
                item.parentNode.removeChild(item);
            }
        });

        this.createCustomSelect(null);
        let self = this;
        document.addEventListener("click", function () {
            self.closeAllSelect(null);
        });
    }

    hideErrorMessage(id: any) {
        const errorElement = document.getElementById(`${id}-error-msg`);
        if (errorElement) {
            errorElement?.classList.add('hidden');
        }
    }

    private async prePurchaseCertificate() {
        try {
            const apiUrl = `${process.env.JS_CORE_API_BASE_URL}/MsMollyDonations/PrePurchase`;
            const request = {
                method: 'POST',
                url: apiUrl,
                data: window?.donorPayload ?? {},
            };

            const isFakeSubmit = window.donorPayload?.donorFirstName.toLowerCase().includes("isfakesubmit")
            if (isFakeSubmit) {
                console.log({mockPayload : window.donorPayload})
                this.isFakeSubmitHandler(request)
                return;
            }
            const prePurchaseRes: any = await apiRequest(request);
            if (prePurchaseRes) {
                const invoicenNumber = prePurchaseRes?.MsMollyDonationId;
                if (invoicenNumber) {
                    const paymentPostUrl = `${apiConfig.paymentPostUrl}?invoicenumber=${invoicenNumber}&appkey=${process.env.JS_DONATION_MERCHANT_KEY}`
                console.log(paymentPostUrl, 'PaymentPostUrl');
                    const step1 = document.querySelectorAll(".contact-us-section.form-step");
                    let form = step1[0] as HTMLFormElement;
                if (form) {
                    form.action = paymentPostUrl;
                    form.method = 'POST';
                    form.submit();
                }
                }
            }
        } catch (err) {
            console.error(err)
        }

    }

    public async getUsState() {
        const baseUrl = `${apiConfig.stateApi}US`;
        const usStateList: any = await cachedAPIWrapper(baseUrl);
        
        // Handle undefined or empty response
        if (!usStateList) {
            this.stateList = {};
            return;
        }
        
        try {
            const stateListResponse = JSON.parse(JSON.stringify(usStateList));
            let obj: StateCodes = stateListResponse.reduce((acc: any, cur: any) => {
                acc[cur.stateCode.trim()] = cur.stateName.trim();
                return acc;
            }, {});
            this.stateList = obj;
            this.createAllStateSelectOption(usStateList);
        } catch (error) {
            console.error('Error processing state data:', error);
            this.stateList = {};
        }
    }

    private createAllStateSelectOption(finalResult: any) {
        const selectState = document.getElementById('mly-donation-state') as HTMLSelectElement;
        if (selectState) {
            while (selectState?.firstChild) {
                selectState.removeChild(selectState.firstChild);
            }
            const option = document.createElement("option");
            option.text = 'Select State';
            option.value = '';
            selectState.appendChild(option);
            finalResult?.forEach((item: any) => {
                const option = document.createElement("option");
                option.text = item.stateName;
                option.value = item.stateCode;
                selectState.appendChild(option);
            });
        }
    }

    private createStateSelectOption(stateCode: any) {
        const isCurrentCustomer = (document.getElementById('mly-customer-yes') as HTMLInputElement)?.checked ? 'choose-state-shelter' : 'choose-state-shelter-no';
        const selectState = document.getElementById(isCurrentCustomer) as HTMLSelectElement;
        const isYesCurrentCustomer = (document.getElementById('mly-customer-yes') as HTMLInputElement)?.checked;
        if (selectState) {
            while (selectState?.firstChild) {
                selectState.removeChild(selectState.firstChild);
            }
            const option = document.createElement("option");
            option.text = isYesCurrentCustomer ? 'Choose from these statewide Molly Maid supported shelters' : 'Choose from these statewide shelters';
            option.value = '';
            selectState.appendChild(option);
            let filteredAndSortedShelters: any;
            if (stateCode) {
                filteredAndSortedShelters = window.shelterList
                    ?.filter((item: any) => item.IsActive && item.State === stateCode)
                    .sort((a: any, b: any) => a.Name.localeCompare(b.Name));
            }

            if (!filteredAndSortedShelters || filteredAndSortedShelters.length === 0) {
                filteredAndSortedShelters = window.shelterList?.filter((item: any) => item.IsActive)
                    .sort((a: any, b: any) => a.Name.localeCompare(b.Name));
            }
            this.constructOutput(selectState, filteredAndSortedShelters);
        }
        const shelterElements = document.querySelectorAll(`#${isCurrentCustomer}`);
        const customSelectParents = Array.from(shelterElements).map(shelterElement => {
            const parentElement = shelterElement.parentElement;
            if (parentElement) {
                let elements = parentElement.querySelectorAll('.select-selected') as NodeList;
                elements.forEach(function (element) {
                    if (element.parentNode) {
                        element.parentNode.removeChild(element);
                    }
                });
            }
            if (parentElement?.classList.contains('custom-select')) {
                return parentElement;
            }
            return null;
        }).filter(parentElement => parentElement !== null);
        this.createCustomSelect(customSelectParents);
    }

    public async getShelterList() {
        const apiUrl = `${process.env.JS_CORE_API_BASE_URL}/MsMollyDonations/GetShelters`;
        const shelterList: any = await getCachedAPIResponse(apiUrl, 10, false);
        const filteredAndSortedShelters = shelterList
            ?.filter((item: any) => item.IsActive)
            .sort((a: any, b: any) => a.Name.localeCompare(b.Name));

        window.shelterList = filteredAndSortedShelters;
        this.createShelterSelectOption(this.sortShelterByStateName(filteredAndSortedShelters));
        this.createStateSelectOption(null);
    }

    private constructOutput(selectState: HTMLSelectElement, filteredAndSortedShelters: any) {
        // Check if selectState is valid
        if (!selectState || !(selectState instanceof HTMLSelectElement)) {
            console.error('Invalid select element provided to constructOutput');
            return;
        }
        
        try {
            const finalResult = this.sortShelterByStateName(filteredAndSortedShelters);
            
            // Handle undefined or empty finalResult
            if (!finalResult || finalResult.length === 0) {
                const option = document.createElement("option");
                option.text = 'No shelters available';
                option.value = "";
                selectState.appendChild(option);
                return;
            }
            
            finalResult.forEach((item: any) => {
                // Skip items with empty or null State
                if (!item || item.State === "" || item.State === null) {
                    return;
                }
                
                // Add state option
                const stateOption = document.createElement("option");
                stateOption.text = `--${item.State}--`;
                stateOption.value = "";
                stateOption.disabled = true;
                selectState.appendChild(stateOption);
                
                // Check if Data is a valid array
                if (!item.Data || !Array.isArray(item.Data) || item.Data.length === 0) {
                    return;
                }
                
                // Add shelter options for this state
                item.Data.forEach((shelter: any) => {
                    if (!shelter?.Name || !shelter?.City || !shelter?.State) {
                        return; // Skip invalid shelter objects
                    }
                    
                    const shelterOption = document.createElement("option");
                    shelterOption.text = `${shelter.Name} - ${shelter.City}, ${shelter.State}`;
                    shelterOption.value = shelter.MsMollyShelterId?.toString() || "";
                    selectState.appendChild(shelterOption);
                });
            });
        } catch (error) {
            console.error('Error in constructOutput:', error);
            
            // Add a fallback option
            const fallbackOption = document.createElement("option");
            fallbackOption.text = 'Error loading shelters';
            fallbackOption.value = "";
            selectState.appendChild(fallbackOption);
        }
    }

    private toSorted(shelters: Shelter[]): Shelter[] {
        // Return empty array if shelters is undefined, null, or not an array
        if (!shelters || !Array.isArray(shelters)) {
            return [];
        }
        
        try {
            return shelters.sort((a, b) => {
                // Handle invalid shelter objects that might not have Name property
                if (!a?.Name) return -1;
                if (!b?.Name) return 1;
                return a.Name.localeCompare(b.Name);
            });
        } catch (error) {
            console.error('Error sorting shelters:', error);
            return [];
        }
    }

    sortShelterByStateName(shelters: Shelter[]): StateData[] {
        // Return empty array if shelters is undefined, null, or not an array
        if (!shelters || !Array.isArray(shelters)) {
            return [];
        }
        
        const cityDataMap: { [key: string]: Shelter[] } = {};
        for (const shelter of shelters) {
            // Skip invalid shelter objects or those without State property
            if (!shelter || typeof shelter !== 'object' || !shelter.State) {
                continue;
            }
            
            if (!cityDataMap[shelter.State]) {
                cityDataMap[shelter.State] = [];
            }
            cityDataMap[shelter.State].push(shelter);
        }
        
        const cityDataArray: StateData[] = [];
        for (const state in cityDataMap) {
            cityDataArray.push({
                State: this.stateList[state] ?? "",
                Data: this.toSorted(cityDataMap[state])
            });
        }
        
        cityDataArray.sort((a, b) => a.State.localeCompare(b.State))
        return cityDataArray;
    }

    private createShelterSelectOption(finalResult: any) {
        const isMlyCustmer = document.querySelector('input[name="mly-customer-radio"]:checked');
        const chooseShelterId = (isMlyCustmer as HTMLInputElement)?.value === 'no' ? 'choose-all-shelter-no' : 'choose-all-shelter';
        const selectState = document.getElementById(chooseShelterId) as HTMLSelectElement | null;
        if (!selectState) {
            console.warn(`Element with ID "${chooseShelterId}" not found.`);
            this.initializeCustomSelect();
            return;
        }
        if (!finalResult || finalResult.length === 0) {
            this.initializeCustomSelect();
            return;
        }
        let props: Record<string, string> = {};
        const attrNames = selectState.getAttributeNames?.();
        if (attrNames) {
            attrNames.forEach(attribute => {
                const value = selectState.getAttribute(attribute);
                if (value !== null) {
                    props[attribute] = value;
                }
            });
        }
        selectState.innerHTML = "";
        const children: (VNode | string)[] = [
            {
                type: 'option',
                props: { value: '' },
                children: ['Choose from these nationwide shelters'],
            }
        ];
        finalResult.forEach((item: any) => {
            if (item.State === "") { return };
            children.push({
                type: 'option',
                props: { value: '', disabled: true },
                children: [`--${item.State}--`]
            });
            item?.Data?.forEach((shelter: any) => {
                if (!shelter?.MsMollyShelterId) return;
                children.push({
                    type: 'option',
                    props: {
                        value: shelter.MsMollyShelterId,
                    },
                    children: [`${shelter.Name} - ${shelter.City}, ${shelter.State}`]
                });
            });
        });
        const parent = selectState.parentElement;
        if (parent) {
            updateVDomElement(parent, {
                type: 'select',
                props,
                children
            }, parseDOMString(selectState.outerHTML), 1);
        }
        this.initializeCustomSelect();
    }


    private switchToNextStep() {
        hmsProgressBarUpdate('100%', 1);
        window.scrollTo(0, 0);
    }

}

export function donationConfirmation(currentUrl: string, resultMsg: string) {
    const donorConfirmation = new DonorConfirmation();
    if (currentUrl.indexOf("?") !== -1) {
        if (resultMsg?.toUpperCase() === "APPROVAL") {
            window.sessionStorage.setItem('donorPaymentResult', resultMsg);
            donorConfirmation.displayMessage(true);
        } else {
            window.sessionStorage.removeItem('donorPaymentResult');
            donorConfirmation.displayMessage(false);
        }
        donorConfirmation.pushHTMLToHistoryAndCleanUrl();
    } else {
        const storedResultMsg = window.sessionStorage.getItem('donorPaymentResult');
        if (storedResultMsg) {
            donorConfirmation.displayMessage(true);
        } else {
            donorConfirmation.displayMessage(false);
        }
    }
    window.onpopstate = (event: PopStateEvent) => {
        if (event.state) {
            document.documentElement.outerHTML = event.state.html;
            document.title = event.state.pageTitle;
        }
    };
}

function donationForm() {    
    const isMlyCustmer = (document.querySelector('input[name="mly-customer-radio"]:checked') as HTMLInputElement)?.value;
    if (isMlyCustmer === 'yes') {
        const isNotMlyCustomer = document.getElementById('mlyCustomerNo') as HTMLElement;
        if (isNotMlyCustomer) {
            isNotMlyCustomer.style.display = 'none';
        }
    } else {
        const isMlyCustomer = document.getElementById('mlyCustomerYes') as HTMLElement;
        if (isMlyCustomer) {
            isMlyCustomer.style.display = 'none';
        }
    }
    Donation.getInstance();
}

// Initialize donation based on DOM readiness
function initializeDonationModule() {
    const conceptIdInput = document.getElementById('conceptId');
    if (!conceptIdInput) return;  
    const conceptId = Number.parseInt((conceptIdInput as HTMLInputElement).value);
    if (conceptId == 1) {

        const currentUrl = window.location.href;
        const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());
        const resultMsg = params.result_msg;
        const donationFormDom = document.querySelector(".donation-form");
        const donationConfirmationDom = document.querySelector(".donation-success");
        if (donationFormDom) {
            donationForm()
        } else if (donationConfirmationDom) {
            donationConfirmation(currentUrl, resultMsg)
        }
    }
}

// Handle both standard loading and chunked loading scenarios
const isDonationAlreadyInitialized = (globalThis as any).__DonationDOMLoader;
if (!isDonationAlreadyInitialized) {
    (globalThis as any).__DonationDOMLoader = true;
    if (document.readyState === 'loading') {
        document.addEventListener("DOMContentLoaded", initializeDonationModule);
    } else {
        initializeDonationModule();
    }
}
